"use client";

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2, Pencil, Plus, PowerOff, RotateCcw, ShieldAlert, X } from "lucide-react";
import { useAdminSession } from "@/hooks/use-admin-session";
import { PHASE_KEYS, PHASE_TITLES, type PhaseKey } from "@/lib/hundert-tage";
import {
  listModuleIdeas,
  saveModuleIdea,
  setModuleIdeaActive,
} from "@/lib/module-ideas.functions";
import type { ModuleIdea } from "@/lib/module-ideas";

export const Route = createFileRoute("/_admin/admin/module-ideas")({
  head: () => ({
    meta: [
      { title: "Ideenpool 100 Tage – Admin – creategy" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ModuleIdeasAdminPage,
});

type FormState = {
  id?: string;
  phase_key: PhaseKey;
  title: string;
  description: string;
  pitfall: string;
  sort_order: string;
};

const emptyForm: FormState = {
  phase_key: "zwischenphase",
  title: "",
  description: "",
  pitfall: "",
  sort_order: "0",
};

const inputClass =
  "mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary";
const labelClass = "block text-sm font-semibold text-foreground";

/** Ticket Z8: Pflegeoberfläche des Ideenkatalogs. */
function ModuleIdeasAdminPage() {
  const session = useAdminSession();
  const isAdmin = session.isAdmin;
  const qc = useQueryClient();

  const listFn = useServerFn(listModuleIdeas);
  const saveFn = useServerFn(saveModuleIdea);
  const activeFn = useServerFn(setModuleIdeaActive);

  const [form, setForm] = useState<FormState | null>(null);

  const { data: rows, isLoading, error } = useQuery({
    queryKey: ["admin-module-ideas"],
    queryFn: () => listFn(),
  });

  const groups = useMemo(() => {
    const list = rows ?? [];
    return PHASE_KEYS.map((phase) => ({
      phase,
      items: list.filter((r) => r.phase_key === phase),
    }));
  }, [rows]);

  const saveMutation = useMutation({
    mutationFn: (state: FormState) =>
      saveFn({
        data: {
          ...(state.id ? { id: state.id } : {}),
          phase_key: state.phase_key,
          title: state.title.trim(),
          description: state.description.trim(),
          pitfall: state.pitfall.trim(),
          sort_order: Number(state.sort_order) || 0,
        },
      }),
    onSuccess: () => {
      toast.success("Idee gespeichert.");
      setForm(null);
      qc.invalidateQueries({ queryKey: ["admin-module-ideas"] });
    },
    onError: (err: Error) => toast.error(err.message || "Speichern fehlgeschlagen."),
  });

  const activeMutation = useMutation({
    mutationFn: (vars: { id: string; active: boolean }) => activeFn({ data: vars }),
    onSuccess: (_data, vars) => {
      toast.success(vars.active ? "Idee aktiviert." : "Idee deaktiviert.");
      qc.invalidateQueries({ queryKey: ["admin-module-ideas"] });
    },
    onError: (err: Error) => toast.error(err.message || "Änderung fehlgeschlagen."),
  });

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            Ideenpool „100 Tage im Amt"
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Der Katalog gilt für alle Kundinnen und Kunden mit freigeschaltetem Modul.
            Status und persönliche Notizen der Amtsträgerinnen und Amtsträger sind hier
            bewusst nicht einsehbar.
          </p>
        </div>
        {isAdmin && (
          <button
            type="button"
            onClick={() => setForm({ ...emptyForm })}
            className="inline-flex items-center gap-2 rounded-md bg-brand-button px-3 py-2 text-sm font-semibold text-brand-button-foreground"
          >
            <Plus className="h-4 w-4" aria-hidden />
            Idee anlegen
          </button>
        )}
      </header>

      {!isAdmin && (
        <p className="rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
          Als Editorin oder Editor können Sie den Ideenpool nur ansehen. Änderungen sind
          Administratoren vorbehalten.
        </p>
      )}

      {isLoading && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Katalog wird geladen …
        </p>
      )}
      {error && (
        <p className="text-sm text-destructive">Der Katalog konnte nicht geladen werden.</p>
      )}

      {form && isAdmin && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (!form.title.trim()) {
              toast.error("Bitte einen Titel angeben.");
              return;
            }
            saveMutation.mutate(form);
          }}
          className="space-y-4 rounded-lg border border-border bg-background p-4"
        >
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-foreground">
              {form.id ? "Idee bearbeiten" : "Neue Idee"}
            </h2>
            <button
              type="button"
              onClick={() => setForm(null)}
              aria-label="Formular schließen"
              className="rounded p-1 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass} htmlFor="idea-phase">
                Phase
              </label>
              <select
                id="idea-phase"
                value={form.phase_key}
                onChange={(e) => setForm({ ...form, phase_key: e.target.value as PhaseKey })}
                className={inputClass}
              >
                {PHASE_KEYS.map((key) => (
                  <option key={key} value={key}>
                    {PHASE_TITLES[key]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass} htmlFor="idea-sort">
                Reihenfolge
              </label>
              <input
                id="idea-sort"
                type="number"
                min={0}
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label className={labelClass} htmlFor="idea-title">
              Titel
            </label>
            <input
              id="idea-title"
              value={form.title}
              maxLength={200}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass} htmlFor="idea-description">
              Beschreibung (Format und Vorgehen)
            </label>
            <textarea
              id="idea-description"
              rows={4}
              maxLength={4000}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass} htmlFor="idea-pitfall">
              Fallstrick
            </label>
            <textarea
              id="idea-pitfall"
              rows={3}
              maxLength={2000}
              value={form.pitfall}
              onChange={(e) => setForm({ ...form, pitfall: e.target.value })}
              className={inputClass}
            />
          </div>

          <button
            type="submit"
            disabled={saveMutation.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground disabled:opacity-60"
          >
            {saveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
            Speichern
          </button>
        </form>
      )}

      <div className="space-y-6">
        {groups.map((group) => (
          <section key={group.phase} className="space-y-2">
            <h2 className="text-lg font-semibold text-foreground">
              {PHASE_TITLES[group.phase]}
            </h2>
            {group.items.length === 0 ? (
              <p className="text-sm text-muted-foreground">Noch keine Ideen hinterlegt.</p>
            ) : (
              <ul className="space-y-2">
                {group.items.map((idea) => (
                  <IdeaAdminCard
                    key={idea.id}
                    idea={idea}
                    isAdmin={isAdmin}
                    onEdit={() =>
                      setForm({
                        id: idea.id,
                        phase_key: idea.phase_key as PhaseKey,
                        title: idea.title,
                        description: idea.description,
                        pitfall: idea.pitfall,
                        sort_order: String(idea.sort_order),
                      })
                    }
                    onToggleActive={() =>
                      activeMutation.mutate({ id: idea.id, active: !idea.active })
                    }
                  />
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}

function IdeaAdminCard({
  idea,
  isAdmin,
  onEdit,
  onToggleActive,
}: {
  idea: ModuleIdea;
  isAdmin: boolean;
  onEdit: () => void;
  onToggleActive: () => void;
}) {
  return (
    <li
      className={`rounded-lg border border-border bg-background p-4 ${
        idea.active ? "" : "opacity-60"
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">
            {idea.title}
            {!idea.active && (
              <span className="ml-2 rounded bg-muted px-2 py-0.5 text-xs font-normal text-muted-foreground">
                deaktiviert
              </span>
            )}
          </p>
          {idea.description && (
            <p className="mt-2 whitespace-pre-line text-sm text-muted-foreground">
              {idea.description}
            </p>
          )}
          {idea.pitfall && (
            <p className="mt-2 flex items-start gap-1 whitespace-pre-line text-sm text-muted-foreground">
              <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              <span>
                <strong className="font-semibold text-foreground">Fallstrick:</strong>{" "}
                {idea.pitfall}
              </span>
            </p>
          )}
        </div>
        {isAdmin && (
          <div className="flex shrink-0 gap-2">
            <button
              type="button"
              onClick={onEdit}
              className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-semibold text-foreground"
            >
              <Pencil className="h-3.5 w-3.5" aria-hidden />
              Bearbeiten
            </button>
            <button
              type="button"
              onClick={onToggleActive}
              className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-semibold text-foreground"
            >
              {idea.active ? (
                <>
                  <PowerOff className="h-3.5 w-3.5" aria-hidden />
                  Deaktivieren
                </>
              ) : (
                <>
                  <RotateCcw className="h-3.5 w-3.5" aria-hidden />
                  Aktivieren
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </li>
  );
}
